export const DEFAULT_HIGHLIGHT_COLOR = '#4200ff';
export const DEFAULT_HIGHLIGHT_COLOR_2 = '#ff0000';
export const DEFAULT_HIGHLIGHT_COLOR_3 = '#72e9c5';
export const DEFAULT_HIGHLIGHT_COLOR_4 = '#fd8bdf';
export const DEFAULT_HIGHLIGHT_COLOR_5 = '#ebef0b';
export const DEFAULT_HIGHLIGHT_COLOR_6 = '#4fba78';

export const DEFAULT_HIGHLIGHT_COLOR_7 = '#f2c95a';

import React from 'react';
import moment from 'moment';
import round from 'lodash/round';
import uniq from 'lodash/uniq';
import sortBy from 'lodash/sortBy';
import indexOf from 'lodash/indexOf';
import Icon from '../Icon/Icon';
import { Amount } from 'src_common/common/amount/Amount';

export function matchStakeFactor(sf, config) {
    const condition = config['highlight.stakeFactorCondition'],
        sf2 = parseFloat(config['highlight.stakeFactor']);

    if (!sf2) {
        return false;
    }

    switch (condition) {
        case '>':
            return sf > sf2;
        case '<':
            return sf < sf2;
        case '=':
            return sf == sf2;
        case '>=':
            return sf >= sf2;
        case '<=':
            return sf <= sf2;
    }

    return false;
}

function highlightStakeFactor(row, config) {
    return matchStakeFactor(row.stakeFactor, config) || parseFloat(row.stakeFactor) < 1
        ? { background: config['highlight.stakeFactorColor'] || DEFAULT_HIGHLIGHT_COLOR_2 }
        : null;
}

const reportedError = new Set();

const convertAmount = (value) => {
    if (typeof value === 'number') {
        return $appState.configState.precision.newFromOld(value).value;
    }

    if (typeof value === 'string') {
        return new Amount(value).value;
    }

    return null;
};

const getChannelColour = (row, config) => {
    if (config['channelDesktop'] === true && row.channel === 'desktop') {
        return {
            background: config['highlight.channelDesktopColor'] || DEFAULT_HIGHLIGHT_COLOR_3,
        };
    } else if (config['channelMobile'] === true && row.channel === 'mobile') {
        return {
            background: config['highlight.channelMobileColor'] || DEFAULT_HIGHLIGHT_COLOR_4,
        };
    } else if (config['channelQuickbet'] === true && row.channel === 'quickbet') {
        return {
            background: config['highlight.channelQuickbetColor'] || DEFAULT_HIGHLIGHT_COLOR_5,
        };
    } else if (config['channelTelebetting'] === true && row.channel === 'telebetting') {
        return {
            background: config['highlight.channelTelebettingColor'] || DEFAULT_HIGHLIGHT_COLOR_6,
        };
    }
};

export const COLUMNS = [
    {
        name: 'placedAt',
        label: 'Placed At',
        minWidth: 160,
        value: (row) => row.placedAt && moment(row.placedAt).format('DD/MM/YYYY HH:mm:ss'),
        style: (row, config) => getChannelColour(row, config),
    },
    {
        name: 'settledAt',
        label: 'Settled At',
        minWidth: 160,
        value: (row) => row.settledAt && moment(row.settledAt).format('DD/MM/YYYY HH:mm:ss'),
        style: (row, config) => getChannelColour(row, config),
    },
    {
        name: 'payout',
        label: 'Payout',
        minWidth: 80,
         value: (row) => convertAmount(row.payout),
    },
    {
        name: 'customer',
        label: 'Customer',
        minWidth: 160,
        route: (data) => `/trading?*customers=${data.account.id}&*customers.parameters={"tab": "customer"}`,
        value: (row) => row.account && row.account.name,
    },
    {
        name: 'stakeFactor',
        label: 'SF',
        minWidth: 40,
        style: (row, config) => highlightStakeFactor(row, config),
    },
    {
        name: 'inPlay',
        label: 'In-play',
        minWidth: 65,
        value: (row) => (row.legs.reduce((value, next) => value && next.inPlay, true) ? 'Y' : 'N'),
    },
    {
        name: 'sport',
        label: 'Sport',
        minWidth: 140,
        value: (row) =>
            uniq(row.legs.map((leg) => leg.sport && leg.sport.name))
                .filter((x) => x)
                .join(', ') || '-',
    },
    {
        name: 'competition',
        label: 'Competition',
        minWidth: 180,
        value: (row) =>
            uniq(row.legs.map((leg) => leg.competition && leg.competition.name))
                .filter((x) => x)
                .join(', ') || '-',
    },
    {
        name: 'event',
        label: 'Event',
        minWidth: 250,
        route: (data) => `/trading?*events=${data.legs[0].event.id}`,
        value: (row) => uniq(row.legs.map((leg) => leg.event.name)).join(', '),
    },
    {
        name: 'market',
        label: 'Market',
        minWidth: 130,
        route: (data) => {
            try {
                const eventId = data.legs[0].event.id;
                const marketId = data.legs[0].market.id;

                return `/trading?*events=${eventId}&*events.parameters={"tab":"markets","market":${marketId}}`;
            } catch (err) {
                const errorId = JSON.stringify(data);

                if (!reportedError.has(errorId)) {
                    reportedError.add(errorId);
                    window.console.error({
                        kind: 'BetTicker Error1',
                        err: err,
                        data: data,
                    });
                }
            }
        },
        value: (row) =>
            uniq(row.legs.map((leg) => leg.market && leg.market.name))
                .filter((x) => x)
                .join(', ') || '-',
    },
    {
        name: 'selection',
        label: 'Selection',
        minWidth: 130,
        route: (data) => {
            try {
                const type = data.legs.length > 1 ? 'multiple' : 'single';
                const eventId = data.legs[0].event.id;
                const marketId = data.legs[0].market.id;

                return `/trading?tab=liabilities&*liabilities=${type}&*liabilities.parameters={"event":${eventId},"market":${marketId}}`;
            } catch (err) {
                const errorId = JSON.stringify(data);

                if (!reportedError.has(errorId)) {
                    reportedError.add(errorId);
                    window.console.error({
                        kind: 'BetTicker Error2',
                        err: err,
                        data: data,
                    });
                }
            }
        },
        value: (row) => uniq(row.legs.map((leg) => leg.selection.name)).join(', '),
    },
    {
        name: 'price',
        label: 'Price',
        minWidth: 80,
        value: (row) => row.legs[0].price && row.legs[0].price.f,
    },
    {
        name: 'decimalPrice',
        label: 'Decimal Price',
        minWidth: 125,
        value: (row) => row.legs[0].price && round(row.legs[0].price.d, 2),
    },
    {
        name: 'priceType',
        label: 'Price type',
        minWidth: 95,
        value: (row) => row.legs[0].priceType.toUpperCase(),
    },
    {
        name: 'ew',
        label: 'EW',
        minWidth: 30,
        value: (row) => row.eachWay !== undefined && (row.eachWay ? 'Y' : 'N'),
    },
    {
        name: 'type',
        label: 'Bet type',
        minWidth: 80,
    },
    {
        name: 'stakePerLine',
        label: 'Stake per line',
        minWidth: 115,
        style: (row, config) => {
            if (config['highlight.betsAbove'] && row.stakePerLine > config['highlight.betsAbove'] * 100) {
                return {
                    background: config['highlight.betsAboveColor'] || DEFAULT_HIGHLIGHT_COLOR,
                };
            }
        },
        value: (row) => {
            return (
                <span>
                    {row.freebet && <Icon name='gift' />}
                    {convertAmount(row.stakePerLine) ?? 0}
                </span>
            );
        },
    },
    {
        name: 'totalStake',
        label: 'Total stake',
        minWidth: 100,
        style: (row, config) => {
            if (config['highlight.betsAbove'] && row.totalStake > config['highlight.betsAbove'] * 100) {
                return {
                    background: config['highlight.betsAboveColor'] || DEFAULT_HIGHLIGHT_COLOR,
                };
            }
        },
        value: (row) => {
            return (
                <span>
                    {row.freebet && <Icon name='gift' />}
                    {convertAmount(row.totalStake) ?? 0}
                </span>
            );
        },
    },
    {
        name: 'maxLineStake',
        label: 'Max line stake',
        minWidth: 130,
        value: (row) => {
            return convertAmount(row.maxBet);
        },
    },
    {
        name: 'stakePerLine/maxAllowedBet',
        label: 'Stake per line/Max allowed bet',
        minWidth: 200,
        style: (row, config) => {
            if (
                config['highlight.maxStakePercentage'] !== undefined &&
                (Boolean(row.maxBet) && parseFloat((100 * row.stakePerLine) / row.maxBet).toFixed(2)) >
                    parseFloat(config['highlight.maxStakePercentage'])
            ) {
                return {
                    background: config['highlight.maxStakePercentageColor'] || DEFAULT_HIGHLIGHT_COLOR_7,
                };
            }
        },
        value: (row) => Boolean(row.maxBet) && `${parseFloat((100 * row.stakePerLine) / row.maxBet).toFixed(2) }%`,
    },
    {
        name: 'channel',
        label: 'Channel',
        minWidth: 80,
        style: (row, config) => getChannelColour(row, config),
    },
    {
        name: 'rejectReason',
        label: 'Reason',
        minWidth: 180,
        value: (data) => {
            const order = [
                'maximum',
                'started',
                'suspended selection',
                'suspended market',
                'suspended event',
                'decrease',
                'increase',
                'bet-stake-too-high',
            ];

            return sortBy(data.remarks, (remark) => {
                const testAgainst =
                    remark.code === 'suspended' ? `${remark.code} ${remark.resource}`.toLowerCase() : remark.code;
                return indexOf(order, testAgainst);
            })
                .map((remark) => `${remark.resource} ${remark.code}`)
                .join('\n');
        },
    },
    {
        name: 'ip',
        label: 'Ip Address',
        minWidth: 320,
    },
];
